// ============================================================
// CENTRAL MARKETING CONTENT — single source of truth for the
// public site. Pricing, nav, features, industries, integrations,
// stats and testimonials all live here. Edit copy here, not in
// the section components.
// ============================================================

// ---------- PRICING (source of truth — also used by /pricing) ----------
export const PLANS = [
  {
    name: "Crew", price: "39.99", seats: "Up to 12 users",
    desc: "Owner-operators and small civil crews.", highlight: false,
    features: ["Unlimited jobs & clients", "Timesheets + payroll", "SSSP generator (AI)", "Fleet tracking", "Branded PDF exports", "Mobile PWA + push notifications", "Email support"],
  },
  {
    name: "Site", price: "59.99", seats: "Up to 30 users",
    desc: "Most NZ civil contractors land here.", highlight: true,
    features: ["Everything in Crew", "Dispatch board + run-sheets", "AI invoice scanner", "Real-time team chat", "Pay run + Xero/MYOB export", "Multi-site dispatch", "Priority support"],
  },
  {
    name: "Yard", price: "99.99", seats: "Up to 50 users",
    desc: "Bigger crews running multiple sites.", highlight: false,
    features: ["Everything in Site", "Materials & inventory", "Org-level analytics", "Free CSV migration", "Dedicated NZ account manager", "Phone support"],
  },
  {
    name: "White Label", price: "199.99", seats: "Up to 50 active users",
    desc: "Your brand. Powered by Fieldlink.", highlight: false,
    features: ["Everything in Yard", "White-label branding entitlement", "Priority support", "Workspace branding controls"],
  },
];
export const ENTERPRISE = {
  name: "Fleet", seats: "50+ users", email: "support@fieldlink.co.nz",
  desc: "Multi-branch operators, head contractors and franchises. We'll tailor a plan to your seat count and rollout.",
};
export const PRICING_BENEFITS = ["All features included", "No setup fee", "Cancel anytime", "NZ support", "No lock-in contracts"];

// ---------- BRAND CLAIMS (keep consistent everywhere) ----------
// HOSTING: single approved statement. Detailed claim (FAQ/Privacy): data hosted
// in MongoDB Atlas Sydney ap-southeast-2 — closest region to NZ. Do NOT claim
// "NZ hosted" — flagged in /app/memory/MARKETING_CONSISTENCY_REPORT.md.
export const HOSTING_CLAIM = "Built in NZ";
export const HOSTING_STATEMENT = "Data hosted in the Sydney (ap-southeast-2) region — the closest cloud region to NZ — encrypted at rest and in transit.";
export const SUPPORT_EMAIL = "support@fieldlink.co.nz";
// TRIAL: one source of truth. Public plan names are Crew/Site/Yard/White Label/Fleet —
// never say "Enterprise trial" on marketing pages.
export const TRIAL = { days: 7, creditCard: false, blurb: "7-day free trial — every feature unlocked, no credit card required." };
export const TRIAL_CTA = "Start 7-day free trial";
export const SIGNUP_REASSURANCE = "Sign up and get straight in. No waiting. We only call if you ask us to.";
export const SIGNUP_FAQ = { q: "Can I start straight after signing up?", a: "Yes. Sign up online and get straight into Fieldlink — no waiting for approval and no sales call required. Start setting up your company and first Job straight away. We only call if you request a call." };
export const TRUST_POINTS = ["No credit card", "NZ support", "Works offline", "Xero connected"];

// ---------- STATS (existing published site claims) ----------
// No verified quantitative customer evidence has been supplied.
export const METRICS = [];
export const METRICS_FOOTNOTE = "";

// ---------- TESTIMONIALS ----------
// TODO(content review): add real, verified customer quotes here.
// The Social Proof section renders a "talk to a crew like yours" panel
// while this list is empty — DO NOT publish invented testimonials.
export const TESTIMONIALS = [];

// ---------- NAVIGATION ----------
export const PRODUCT_MENU = [
  { group: "Commercial", items: [
    ["Takeoffs", "/#explorer"], ["Quotes", "/#explorer"], ["Rate Library", "/#explorer"],
    ["Variations", "/#explorer"], ["Customer Invoices", "/#explorer"], ["Job Financials", "/#explorer"],
  ]},
  { group: "Projects", items: [
    ["Jobs", "/job-management-nz"], ["Project Control", "/#explorer"], ["Trucking dispatch", "/#explorer"],
    ["Crew Planning", "/#explorer"], ["Materials", "/#explorer"],
  ]},
  { group: "Field", items: [
    ["Mobile App", "/download"], ["Timesheets", "/timesheet-app-nz"], ["Running Sheets", "/#explorer"],
    ["Site Diary", "/#explorer"], ["Photos", "/#explorer"], ["Forms", "/#explorer"],
  ]},
  { group: "Safety & QA", items: [
    ["SSSPs", "/sssp-generator-nz"], ["Toolbox Talks", "/safety-management-nz"], ["Pre-Starts", "/pre-start-checklist-app-nz"],
    ["Site Inspections", "/#explorer"], ["QA Records", "/#explorer"], ["Competencies", "/#explorer"],
  ]},
  { group: "Plant & Assets", items: [
    ["Fleet", "/fleet-management-nz"], ["Live Tracking", "/fleet-management-nz"], ["Fuel", "/#explorer"], ["Gear", "/#explorer"],
  ]},
  { group: "People & Finance", items: [
    ["Team & Leave", "/#explorer"], ["Payroll", "/payroll-software-civil-nz"], ["Job Costing", "/#explorer"],
    ["Supplier Bills", "/#explorer"], ["Xero Integration", "/xero-timesheet-integration-nz"],
  ]},
];

export const SOLUTIONS_MENU = [
  ["Civil Contractors", "/civil-construction-software-nz"],
  ["Earthworks", "/#industries"],
  ["Drainage", "/#industries"],
  ["Roading", "/#industries"],
  ["Cartage / Trucking", "/#industries"],
  ["Utilities & Infrastructure", "/#industries"],
];

export const WHY_MENU = [
  ["Why Fieldlink", "/#why"],
  ["Fieldlink vs Simpro", "/simpro-alternative-nz"],
  ["Security", "/security"],
  ["FAQ", "/faq"],
];

export const RESOURCES_MENU = [
  ["Free guides & calculators", "/resources"],
  ["Drainage bedding guide", "/guides/how-to-calculate-pipe-bedding"],
  ["Crew productivity calculator", "/tools/crew-productivity-calculator"],
  ["Blog", "/blog"],
  ["Find contractors (directory)", "/contractors"],
  ["Get the mobile app", "/download"],
  ["FAQ", "/faq"],
  ["Xero integration", "/xero-timesheet-integration-nz"],
];

// ---------- WORKFLOW ----------
export const WORKFLOW = [
  { n: "01", t: "Win It", d: "Takeoffs, estimates and quotes that help you win more work." },
  { n: "02", t: "Plan It", d: "Jobs, crews, plant and dispatch — planned and ready to roll." },
  { n: "03", t: "Build It", d: "Field app for timesheets, site diaries, photos and materials." },
  { n: "04", t: "Keep It Safe", d: "SSSPs, pre-starts, toolbox talks and inspections — all in one place." },
  { n: "05", t: "Know the Numbers", d: "Real-time costs, variations and job margin visibility." },
  { n: "06", t: "Get Paid", d: "Progress claims, invoices and payments connected to Xero." },
];

// ---------- PRODUCT EXPLORER TABS ----------
export const EXPLORER_TABS = [
  {
    id: "commercial", label: "Commercial", heading: "Takeoff to final claim.",
    desc: "Accurate takeoffs, quotes and progress claims that keep your margins on track.",
    features: ["Digital takeoffs & measurements", "Rate library & build-ups", "Quotes & variations", "Progress claims & invoicing", "Job budgets & financials"],
    cta: ["Explore commercial tools", "/register"], visual: "takeoff",
  },
  {
    id: "projects", label: "Projects", heading: "Run every job from one place.",
    desc: "Jobs, project control, scheduling, dispatch, variations, documents and photos — connected to the crew doing the work.",
    features: ["Jobs & project control", "Scheduling & dispatch", "Crew planning", "Variations", "Documents & photos"],
    cta: ["See job management", "/job-management-nz"], visual: "shot", shot: "/screens/office-overview-dark.png", alt: "Fieldlink office overview — live jobs, timesheets and invoices",
  },
  {
    id: "field", label: "Field", heading: "Everything the crew needs. Nothing they don't.",
    desc: "An offline-capable app that installs from the browser — no app store, no chasing updates.",
    features: ["Today's jobs & tasks", "Timesheets & running sheets", "Pre-starts & site diary", "Photos & materials", "Works offline"],
    cta: ["Get the field app", "/download"], visual: "phones", shots: ["/screens/field-home.png", "/screens/field-hours.png"],
  },
  {
    id: "safety", label: "Safety", heading: "Safety paperwork without the paperwork.",
    desc: "SSSPs, toolbox talks, pre-starts and inspections — keep site records available to the field and office.",
    features: ["SSSPs & toolbox talks", "Pre-starts & inspections", "QA records & forms", "Competency register", "Signed on the phone"],
    cta: ["See safety tools", "/safety-management-nz"], visual: "shot", shot: "/screens/office-sssps.png", alt: "Fieldlink SSSP register",
  },
  {
    id: "plant", label: "Plant", heading: "Know where your gear is working.",
    desc: "Fleet, live tracking, fuel, gear and materials — with pre-starts and service reminders built in.",
    features: ["Fleet register & pre-starts", "Live GPS tracking", "Fuel logs & burn rates", "Gear & small tools", "WoF / service reminders"],
    cta: ["See fleet tools", "/fleet-management-nz"], visual: "plant",
  },
  {
    id: "people", label: "People", heading: "Your workforce, connected.",
    desc: "Timesheets flow from the field to approval to payroll export without re-typing a single hour.",
    features: ["Timesheets & approvals", "Team directory & leave", "Payroll export (Xero/MYOB)", "Competencies", "Crew performance"],
    cta: ["See timesheets", "/timesheet-app-nz"], visual: "shot", shot: "/screens/office-timesheets.png", alt: "Fieldlink timesheet approvals",
  },
  {
    id: "finance", label: "Finance", heading: "Know the numbers before the job finishes.",
    desc: "Budgets, actual costs, variations and margin — with claims and invoices pushed to Xero as drafts.",
    features: ["Job budgets vs actuals", "Variations & margin", "Progress claims & invoices", "Supplier bills (AI scanned)", "Xero connected"],
    cta: ["Start free trial", "/register"], visual: "finance",
  },
];

// ---------- INDUSTRIES ----------
export const INDUSTRIES = [
  { t: "Earthworks", img: "/marketing/earthworks.jpg", d: "Track cut/fill, cartage, plant hours and job costing." },
  { t: "Drainage", img: "/marketing/drainage.jpg", d: "Manage pipework, manholes, QA and progress claims." },
  { t: "Roading", img: "/marketing/roading.jpg", d: "Crews, plant, pavement quantities and inspections." },
  { t: "Cartage", img: "/marketing/cartage.jpg", d: "Dispatch trucks, dockets, loads and tonnes." },
  { t: "Civil Contractors", img: "/marketing/civil.jpg", d: "From quote to final account. All in one system." },
];

// ---------- INTEGRATIONS (genuine only) ----------
export const INTEGRATIONS = [
  { name: "Xero", d: "Invoices push as drafts. Payroll export from approvals." },
  { name: "MYOB", d: "Pay run export for NZ payroll." },
  { name: "API & Webhooks", d: "Open data — talk to us about integrations." },
];

// ---------- WHY FIELDLINK ----------
export const WHY_POINTS = [
  { t: "Built for civil", d: "Takeoffs, cartage, plant and progress claims — not a generic trades app." },
  { t: "Designed for NZ", d: "Built in NZ — NZD pricing, GST, Xero and Kiwi support." },
  { t: "Office + field connected", d: "The crew's phone and the office screen share one live system." },
  { t: "Works offline", d: "Built for dead spots. Always catches up at the gate." },
  { t: "Everything together", d: "Commercial, operations, safety, plant and people in one place." },
  { t: "Simple flat pricing", d: "One monthly fee. No per-seat clip. No lock-in contracts." },
];
